import { BrowserModule } from "@angular/platform-browser";
import { APP_INITIALIZER, NgModule, ErrorHandler, CUSTOM_ELEMENTS_SCHEMA, provideZoneChangeDetection } from "@angular/core";
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from "@angular/common/http";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";

import { AppComponent } from "./app.component";
import { HeaderComponent } from "./shared-components/header/header.component";
import { FooterComponent } from "./shared-components/footer/footer.component";
import { HomeComponent } from "./features/home/home.component";
import { MainMenuComponent } from "./shared-components/main-menu/main-menu.component";
import { EngagementComponent } from "./features/engagement/viewall-engagement/engagement.component";

import { AddEditEngagementComponent } from "./features/engagement/add-edit/add-edit.component";
import { HelpComponent } from "./features/help/help.component";
import { UploadSourcefileComponent } from "./features/upload-sourcefile/upload-sourcefile.component";
import { UploadProgressComponent } from "./shared-components/upload-progress/upload-progress.component";
import { CommonModule, DatePipe } from "@angular/common";
import { ViewSingleEngagementComponent } from "./features/engagement/viewsingle/view-singleengagement.component";
import { PreviewSourcefileComponent } from "./features/preview-sourcefile/preview-sourcefile.component";
import { TableModule } from "primeng/table";
import { SelectModule } from "primeng/select";
import { PaginatorModule } from "primeng/paginator";

import { PlugandplayComponent } from "./features/plugandplay/plugandplay.component";
import { OrderListModule } from "primeng/orderlist";

// PrimeNg
import { PrimeNGModule } from "./shared-modules/primeng.module";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ViewadjustComponent } from "./features/adjustment/viewadjust/viewadjust.component";

// Ngx Modules
import { NgxModule } from "./shared-modules/ngx.module";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";

// Services
import { HelpvideosService } from "./services/helpvideos.service";
import { Sharedservice } from "./shared-components/shared.service";
import { EngagementService } from "./services/engagement.service";
import { PbiWorkSpaceService } from "./services/pbiworkspace.service";
import { CurrencyService } from "./services/currency.service";
import { MessageService, ConfirmationService, SharedModule } from "primeng/api";
import { AppRoutingModule } from "./routes/app.routing.module";
import { MonthService } from "./services/month.service";
import { MasterPeriodIdentifierService } from "./services/masterPeriodIdentifier.service";
import { UserService } from "./services/user.service";
import { ExcelService } from "./services/excel.service";
import { StepwizardComponent } from "./features/stepwizard/stepwizard.component";
import { InputCaptureComponent } from "./features/inputcapture/inputcapture.component";
import { ScenarioresultComponent } from "./features/scenarioresult/scenarioresult.component";
import { ScenarioExecutionService } from "./services/scenarioexecution.service";
import { ScenarioDetectionService } from "./services/scenariodetection.service";
import { TabsModule } from "ngx-bootstrap/tabs";
import { AccordionModule } from "primeng/accordion";
import { AddEditadjustmentComponent } from "./features/adjustment/addadjustment/addadjustment.component";
import { LegalEntityService } from "./services/legalentity-service";
import { SelectadjustmentComponent } from "./features/selectadjustment/selectadjustment.component";
import { SourcefilecolumnComponent } from "./features/sourcefilecolumn/sourcefilecolumn.component";
import { RegionService } from "./services/region.service";
import { FlatfileComponent } from "./features/flatfile/flatfile.component";
import { MappingselectionComponent } from "./features/mappingselection/mappingselection.component";

import { DatabookComponent } from "./features/databook/databook.component";
import { RemediationComponent } from "./features/remediation/remediation.component";
import { FlatfileService } from "./services/flatfile.service";

import { MappingService } from "./services/mapping.service";
import { PerformdatacheckComponent } from "./features/performdatacheck/performdatacheck.component";
import { SchedulesComponent } from "./features/schedules/schedules.component";
import { ScheduleService } from "./services/schedule.service";

import { TreeTableModule } from "primeng/treetable";
import { AdjustmentService } from "./services/adjustment.service";
import { AdjustmentCategoryService } from "./services/adjustmentCategory.service";
import { AdjustmentCategoryTypeService } from "./services/adjustmentCategoryType.service";
import { MasterMappingHeadersService } from "./services/masterMappingHeaders.service";
import { AccountService } from "./services/account.service";
import { PeriodService } from "./services/period.service";

import { CoverIndexService } from "./services/coverindex.service";
import { ValidationsComponent } from "./features/validations/validations.component";
import { ValidationsService } from "./services/validations.service";
import { MappingRemediationService } from "./services/mappingRemediation.service";
import { DialogModule } from "primeng/dialog";

import { InterceptorService } from "./services/interceptor.service";
import { FieldsetModule } from "primeng/fieldset";
import { PopupComponent } from "./features/popup/popup.component";
import { RadioButtonModule } from "primeng/radiobutton";
import { DownloadUploadFlatfileComponent } from "./features/download-upload-flatfile/download-upload-flatfile.component";
import { UploadDatabookComponent } from "./features/upload-databook/upload-databook.component";
import { MasterHelpService } from "./services/masterhelp.service";
import { PopupconfirmComponent } from "./features/popupconfirm/popupconfirm.component";
import { PopupconfirmdataTabComponent } from "./features/popupconfirmDataTab/popupconfirmDataTab.component";
import { SelectregionComponent } from "./features/selectregion/selectregion.component";
import { SessionExpiredComponent } from "./features/sessionExpired/sessionExpired.component";
import { PrivacypolicyComponent } from "./features/privacypolicy/privacypolicy.component";
import { UnauthorizeComponent } from "./features/unauthorize/unauthorize.component";
import { ErrorpopupComponent } from "./features/errorpopup/errorpopup.component";
import { AuditingService } from "./services/auditing.service";
import { AnalyticsReportComponent } from "./features/analytics-report/analytics-report.component";
import { UserPermissionService } from "./services/userPermissions.service";
import { environment } from "../environments/environment";
import { LoggerModule, NgxLoggerLevel } from "ngx-logger";
import { WebJobQueueService } from "./services/webjobqueue.service";
import { LoadEngagementComponent } from "./features/loadengagement/loadengagement.component";
import { ManualdatabookaddtoqueueComponent } from "./features/manualdatabookaddtoqueue/manualdatabookaddtoqueue.component";
import { DeleteprojectdataComponent } from "./features/deleteprojectdata/deleteprojectdata.component";
import { InfraService } from "./services/infra.service";
import { ComparedatabookComponent } from "./features/comparedatabook/comparedatabook.component";
import { CountryResolver } from "./interfaces/countryResolver";
import { AllwebjobstatusComponent } from "./features/allwebjobstatus/allwebjobstatus.component";
import { GlobalErrorHandlerService } from "./services/globalErrorHandler.service";
import { NgZoneRestoreInterceptor } from "./services/ngzone-restore.interceptor";
import { UserIdleModule } from "angular-user-idle";
import { ReactivateEngagementComponent } from "./features/engagement/reactivate-engagement/reactivate-engagement.component";
import { SafeHtmlPipe } from "./shared-components/safe-html.pipe";

// Ngx-Translate
import { TranslateModule, TranslateLoader, TranslateService } from "@ngx-translate/core";
import { LanguageConstants } from "./shared-components/constant";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";

import { SettingsProvider } from "./settings-provider";
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService
} from "@azure/msal-angular";
import { Configuration } from "msal";
import { AppConfigurationService } from "./services/appConfiguration.service";
//import { ProductService } from './services/productservice';

import { PlugAndPlayService } from "./services/plugandplay.service";
import { CustomizedLayoutsComponent } from './features/customized-layouts/customized-layouts.component';
import { CustomizedLayoutService } from "./services/customized-layout.service";
import { ProgressBarModule } from "primeng/progressbar";
import { MenuModule } from "primeng/menu";
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, PublicClientApplication } from "@azure/msal-browser";

const isIE =
  window.navigator.userAgent.indexOf("MSIE ") > -1 ||
  window.navigator.userAgent.indexOf("Trident/") > -1;


export function init(settingsProvider: SettingsProvider) {
  return settingsProvider.loadConfig();
}

// export function MSALConfigFactory(
//   settingsProvider: SettingsProvider
// ): Configuration {
//   return settingsProvider.MSALConfig(isIE);
// }


// export function MSALAngularConfigFactory(
//   settingsProvider: SettingsProvider
// ): MsalGuardConfiguration {
//   return settingsProvider.MSALAngularConfig();
// }

// export function MSALInstanceFactory(): IPublicClientApplication {
//   return new PublicClientApplication({
//     auth: {
//       clientId: environment.clientId,
//       authority: environment.authority,
//       redirectUri: environment.redirectUri,
//       navigateToLoginRequestUrl: true
//     },
//     cache: {
//       cacheLocation: BrowserCacheLocation.LocalStorage,
//     },
//     system: {
//       allowNativeBroker: false
//     }
//   })
// }


export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
 // const protectedResourceMap = new Map<string, Array<string>>();
 // protectedResourceMap.set(environment.apiUrl, [environment.clientId + '/.default'] );
  return {
    interactionType: InteractionType.Redirect,
    'protectedResourceMap': new Map<string, Array<string> | null>([
       [environment.apiUrl, [environment.clientId + '/.default']],
       [environment.usApiUrl, [environment.clientId + '/.default']],
       [environment.apacApiUrl, [environment.clientId + '/.default']],
       ]),
  }
}
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.clientId,
      authority: environment.authority,
      redirectUri: environment.redirectUri,
      navigateToLoginRequestUrl: true
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
    }
  });
}

export function initializeMsalFactory(msalInstance: IPublicClientApplication) {
  return () => msalInstance.initialize();
}

// Route components read translateService.translations[lang] synchronously in ngOnInit.
// Loading the bundle here makes it available before the first route activates; otherwise
// those reads return undefined and the component throws. fetch is used rather than
// HttpClient so the request cannot be intercepted by MsalInterceptor, which is not yet
// initialised at this point.
export function initializeTranslationsFactory(translate: TranslateService) {
  const fallbackLang = LanguageConstants.US_ENG_LOCALE;
  const storedLang = localStorage.getItem(LanguageConstants.LANG_CODE);
  // Components index translations[] by the stored locale, so that bundle has to be present
  // too, not just the fallback.
  const langs =
    storedLang && storedLang !== fallbackLang ? [fallbackLang, storedLang] : [fallbackLang];
  return async () => {
    for (const lang of langs) {
      try {
        const response = await fetch(new URL(`assets/i18n/${lang}.json`, document.baseURI));
        if (response.ok) {
          translate.setTranslation(lang, await response.json(), true);
        }
      } catch {
        // AppComponent's guarded http.get stays as the fallback.
      }
    }
    translate.setDefaultLang(fallbackLang);
  };
}

  /**
   * Configuring the AAD login type and api related permissions
   * @returns AAD Guard configuration
   */
  export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
      interactionType: InteractionType.Redirect,
      authRequest: {
        scopes: [environment.clientId +'/.default']
      },
      loginFailedRoute: '/'
    }
  }

/**
 * Interceptor configuration for the API calls
 * @returns 
 */

export function MSALInterceptorConfig(): MsalInterceptorConfiguration {
  //const protectedResourceMap = new Map<string, Array<string>>();
  //protectedResourceMap.set(environment.apiUrl,environment.usApiUrl)
    // [environment.usApiUrl, [environment.clientId + '/.default']],
    // [environment.apacApiUrl, [environment.clientId + '/.default')
  return {
    interactionType: InteractionType.Redirect,
    'protectedResourceMap': new Map<string, Array<string> | null>([
       [environment.apiUrl, [environment.clientId + '/.default']],
       [environment.usApiUrl, [environment.clientId + '/.default']],
       [environment.apacApiUrl, [environment.clientId + '/.default']],
       ]),
  }
}

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, "../assets/i18n/", ".json");
}

@NgModule({
  declarations: [
    AppComponent,
    SelectregionComponent,
    FooterComponent,
    HeaderComponent,
    HomeComponent,
    MainMenuComponent,
    EngagementComponent,
    AddEditEngagementComponent,
    UploadSourcefileComponent,
    HelpComponent,
    UploadProgressComponent,
    ViewSingleEngagementComponent,
    PreviewSourcefileComponent,
    StepwizardComponent,
    InputCaptureComponent,
    ScenarioresultComponent,
    AddEditadjustmentComponent,
    SelectadjustmentComponent,
    SourcefilecolumnComponent,
    MappingselectionComponent,
    DatabookComponent,
    FlatfileComponent,
    LoadEngagementComponent,
    RemediationComponent,
    PerformdatacheckComponent,
    RemediationComponent,
    ViewadjustComponent,
    SchedulesComponent,
    ValidationsComponent,
    PopupComponent,
    DownloadUploadFlatfileComponent,
    UploadDatabookComponent,
    PopupconfirmComponent,
    PopupconfirmdataTabComponent,
    SessionExpiredComponent,
    PrivacypolicyComponent,
    ErrorpopupComponent,
    AnalyticsReportComponent,
    UnauthorizeComponent,
    ManualdatabookaddtoqueueComponent,
    DeleteprojectdataComponent,
    ComparedatabookComponent,
    AllwebjobstatusComponent,
    ReactivateEngagementComponent,
    PlugandplayComponent,
    CustomizedLayoutsComponent,
    SafeHtmlPipe,
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    NgxModule,
    PrimeNGModule,
    ConfirmDialogModule,
    TreeTableModule,
    TableModule,
    PaginatorModule,
    FieldsetModule,
    RadioButtonModule,
    NgbModule,
    OrderListModule,
    ProgressBarModule,
    MenuModule,
    CommonModule,
    DialogModule,
    SelectModule,
    ProgressBarModule,
    HttpClientModule,
    SharedModule,
    //FeatureModule,
    MsalModule,
    TabsModule,
    LoggerModule.forRoot({
      level: NgxLoggerLevel.INFO,
      serverLogLevel: NgxLoggerLevel.OFF,
      disableConsoleLogging: false,
    }),
    UserIdleModule.forRoot({
      idle: environment.sessionTimeout,
      timeout: 60,
      ping: 120,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader, // Exported factory function needed for AOT compilation.
        deps: [HttpClient],
      },
    }),
    // MsalModule.forRoot(
    //   new PublicClientApplication({
    //     auth: {
    //       clientId: environment.clientId,
    //       authority: environment.authority,
    //       redirectUri: environment.webAppUrl
    //     },
    //     cache: {
    //       cacheLocation: "localStorage",
    //       storeAuthStateInCookie: isIE,
    //     },
    //      system: {
    //        allowNativeBroker: false, // Disables native brokering support
    //    }
    //   }),
     
    //   {
    //     interactionType: InteractionType.Redirect, // Msal Guard Configuration
    //     authRequest: {
    //       scopes: ["environment.clientId + '/.default'"],
    //     },
    //   },
    //   {
    //     interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
    //     // protectedResourceMap: new Map([
    //     //   ["https://graph.microsoft.com/v1.0/me", ["user.read"]]
    //     // ]),

    //    // unprotectedResources: ['https://www.microsoft.com/en-us/'],

    //     'protectedResourceMap': new Map<string, Array<string> | null>([
   
    //       [environment.apiUrl, [environment.clientId + '/.default']],
   
    //       [environment.usApiUrl, [environment.clientId + '/.default']],
   
    //       [environment.apacApiUrl, [environment.clientId + '/.default']],
   
    //       ]),
    
        
    //   }
   
    // ),
    MsalModule
  ],
  providers: [
    InfraService,
    EngagementService,
    CurrencyService,
    Sharedservice,
    MessageService,
    MonthService,
    MasterPeriodIdentifierService,
    UserService,
    ExcelService,
    DatePipe,
    ScenarioExecutionService,
    ScenarioDetectionService,
    LegalEntityService,
    ConfirmationService,
    RegionService,
    FlatfileService,
    MappingService,
    AdjustmentService,
    AdjustmentCategoryService,
    AdjustmentCategoryTypeService,
    MasterMappingHeadersService,
    AccountService,
    PeriodService,
    ScheduleService,
    CoverIndexService,
    ValidationsService,
    MasterHelpService,
    MappingRemediationService,
    AuditingService,
    UserPermissionService,
    WebJobQueueService,
    CountryResolver,
    GlobalErrorHandlerService,
    HelpvideosService,
    AppConfigurationService,
    PbiWorkSpaceService,
    PlugAndPlayService,
    CustomizedLayoutService,
    TranslateService, 
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },

    // Angular 21's bootstrapModule defaults to zoneless change detection
    // (PlatformRef.bootstrapModuleFactory injects provideZonelessChangeDetectionInternal,
    // which binds NgZone to NoopNgZone). This application predates signals and
    // relies on Zone.js to repaint after async work, so without this every
    // state change made from an HTTP callback, timer or promise stays invisible
    // until an unrelated DOM event happens to trigger a pass.
    provideZoneChangeDetection(),

    SettingsProvider,
    // Must stay ahead of MsalInterceptor so it sits closest to the subscriber
    // on the response path.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NgZoneRestoreInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsalFactory,
      deps: [MSAL_INSTANCE],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslationsFactory,
      deps: [TranslateService],
      multi: true
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [SelectregionComponent,MsalRedirectComponent],
})
export class AppModule {}
